// Single source of truth for profile-pic fetch de-duplication.
//
// Both the lazy-load hook (useWaContactsLazyLoad, which batch-fetches pics for
// the visible range of a contact list) and each self-fetching ContactAvatar
// share these module-level sets so the same contact is never fetched twice —
// no matter how many avatars/lists are mounted on the page at once.

// How long to cache a "no pic" result before re-checking (24 hours).
export const PROFILE_PIC_TTL_MS = 24 * 60 * 60 * 1000;

// Contact IDs we've already kicked off a fetch for this session.
const requestedImages = new Set<string>();
// Contact IDs we've allowed exactly one retry for after an expired URL.
const retriedImages = new Set<string>();

export const profilePicFetchRegistry = {
    has: (contactId: string): boolean => requestedImages.has(contactId),
    add: (contactId: string): void => {
        requestedImages.add(contactId);
    },
    remove: (contactId: string): void => {
        requestedImages.delete(contactId);
    },
    // #803: ids the engine never answered (page-side timeout) are not "checked" — release them so a
    // later render re-requests them instead of waiting out the TTL.
    releaseUnanswered: (requested: readonly string[], answeredIds: readonly string[]): void => {
        const answered = new Set(answeredIds);
        requested.forEach((id) => {
            if (!answered.has(id)) requestedImages.delete(id);
        });
    },
    // After ContactAvatar detects an expired URL we allow ONE re-request. The
    // retriedImages guard makes this idempotent so a permanently-broken URL
    // can't loop forever.
    allowRetry: (contactId: string): void => {
        if (!retriedImages.has(contactId)) {
            retriedImages.add(contactId);
            requestedImages.delete(contactId);
        }
    },
    // True when a recent "no pic" check is still within TTL → skip re-fetch.
    isFresh: (profilePicLastUpdate?: string): boolean => {
        if (!profilePicLastUpdate) return false;
        return Date.now() - new Date(profilePicLastUpdate).getTime() < PROFILE_PIC_TTL_MS;
    },
};
